import React,{useState,useEffect,useReducer,lazy} from 'react';

// Gestion du Context API : state + reducer
import GlobalContext, {reducer,REDUCER_ACTIONS,INITIAL_STATE} from './contexts/global';
import {
  UsersFiltersContext,
  usersFiltersReducer,
  USERS_FILTERS_INITIAL_STATE,

  CollaboratorsFiltersContext,
  collaboratorsFiltersReducer,
  COLLABORATORS_FILTERS_INITIAL_STATE,

  OOODaysFiltersContext,
  ooodaysFiltersReducer,
  OOODAYS_FILTERS_INITIAL_STATE,

  BillingCustomersFiltersContext,
  billingCustomersFiltersReducer,
  BILLING_CUSTOMERS_FILTERS_INITIAL_STATE,

  CustomersFiltersContext,
  customersFiltersReducer,
  CUSTOMERS_FILTERS_INITIAL_STATE,

  ChargesFiltersContext,
  chargesFiltersReducer,
  CHARGES_FILTERS_INITIAL_STATE,

  ActivitiesFiltersContext,
  activitiesFiltersReducer,
  ACTIVITIES_FILTERS_INITIAL_STATE,

  PlanificationFiltersContext,
  planificationFiltersReducer,
  PLANIFICATION_FILTERS_REDUCER_ACTIONS,
  PLANIFICATION_FILTERS_INITIAL_STATE,
} from './contexts/filters';
import ChargesReportContext, {reducer as chargesReportReducer,INITIAL_STATE as CHARGES_REPORT_INITIAL_STATE} from './contexts/charges-report';

// Gestion des routes de l'app
import {RouterProvider} from 'react-router-dom';
import router from './routes/router';

// Services de l'app
import I18nService from './services/I18nService';
import Logger from './services/LoggerService';
import {getHello, getOrganisationsSets} from './services/api/ApiServices';
import I_HELLO from './services/api/interfaces/I_HELLO';
import {readUrlParam} from './helpers/navigationHelper';
import {
  connectMicrosoft,
} from './services/api/ApiServices';
import I_LOGIN from './services/api/interfaces/I_LOGIN';
import APP_CONFIG from './config';

// hooks
import useLocalStorage from './hooks/useLocalStorage';

// query
import {QueryClient,QueryClientProvider} from '@tanstack/react-query';

// Toastify
import { ToastContainer,toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './toastify.css';

// themes
import { customGlobalTheme } from './helpers/themeHelper';
import { ThemeProvider } from "@material-tailwind/react";
import I_ORGANISATIONS_SET from 'services/api/interfaces/I_ORGANISATIONS';
import { getDefaultUserOrganisation, isValidUserOrganisationSelections } from 'helpers/dataHelper';
import { applyUserOrganisations } from 'helpers/servicesHelper';
import { getEnvVariables } from 'helpers/envVariablesHelper';


const Centered = lazy(() => import('./tsx/templates/Centered'));
const ContentsLoading = lazy(() => import('./tsx/contents/ContentsLoading'));
const ContentsServerDown = lazy(() => import('./tsx/contents/ContentsServerDown'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
		queries: {
			staleTime: 1000 * 30  // duree avant que la donnée soit depreciee -- doit etre inferieur a la duree de validite des token
    }
	}
});

function App() {

  // state local
  const [appLoaded,setAppLoaded] = useState<boolean>(false);

  // valeurs dans le reducer
  const [reducerState,reducerDispatch] = useReducer(reducer,INITIAL_STATE);
  const [usersFiltersReducerState,usersFiltersReducerDispatch] = useReducer(usersFiltersReducer,USERS_FILTERS_INITIAL_STATE);
  const [collaboratorsFiltersReducerState,collaboratorsFiltersReducerDispatch] = useReducer(collaboratorsFiltersReducer,COLLABORATORS_FILTERS_INITIAL_STATE);
  const [ooodaysFiltersReducerState,ooodaysFiltersReducerDispatch] = useReducer(ooodaysFiltersReducer,OOODAYS_FILTERS_INITIAL_STATE);
  const [billingCustomersFiltersReducerState,billingCustomersFiltersReducerDispatch] = useReducer(billingCustomersFiltersReducer,BILLING_CUSTOMERS_FILTERS_INITIAL_STATE);
  const [customersFiltersReducerState,customersFiltersReducerDispatch] = useReducer(customersFiltersReducer,CUSTOMERS_FILTERS_INITIAL_STATE);
  const [chargesFiltersReducerState,chargesFiltersReducerDispatch] = useReducer(chargesFiltersReducer,CHARGES_FILTERS_INITIAL_STATE);
  const [activitiesFiltersReducerState,activitiesFiltersReducerDispatch] = useReducer(activitiesFiltersReducer,ACTIVITIES_FILTERS_INITIAL_STATE);
  const [planificationFiltersReducerState,planificationFiltersReducerDispatch] = useReducer(planificationFiltersReducer,PLANIFICATION_FILTERS_INITIAL_STATE);
  const [chargesReportReducerState,chargesReportReducerDispatch] = useReducer(chargesReportReducer,CHARGES_REPORT_INITIAL_STATE);

  // gestion du localStorage
  const {
    getAccessTokenInLocalStorage,
    getRefreshTokenInLocalStorage,
    getRoleInLocalStorage,
    getFunctionInLocalStorage,
    setAccessTokenInLocalStorage,
    setRefreshTokenInLocalStorage,
    setRoleInLocalStorage,
    setFunctionInLocalStorage,
  } = useLocalStorage(reducerDispatch);

  // Actions réalisées une seule fois au chargement de l'appli
  useEffect(() => {

    // Init des services
    I18nService.getInstance().init('fr');
    const LOG = Logger.getInstance();


    let accessToken: string | undefined = undefined;
    let refreshToken: string | undefined = undefined;
    let role: string | undefined = undefined;
    let fonction: string | undefined = undefined;



    // chargement des variables server
    getEnvVariables({names:[
      'REACT_APP_API_URL',
      'REACT_APP_API_TOKEN',
      'REACT_APP_SAML_ENABLED',
      'REACT_APP_LOCALSTORAGE_PREFIX',
      'REACT_APP_LOCALSTORAGE_ACTIVE'
    ]})
      .then((vars) => {
        if (vars) {
          APP_CONFIG.API_URL = vars.REACT_APP_API_URL;
          APP_CONFIG.API_KEY = vars.REACT_APP_API_TOKEN;
          APP_CONFIG.ENDPOINT_SUFFIX = vars.REACT_APP_API_ENDPOINT_SUFFIX ?? '';
          APP_CONFIG.SAML_ENABLED = vars.REACT_APP_SAML_ENABLED;

          APP_CONFIG.LOCALSTORAGE_PREFIX = vars.REACT_APP_LOCALSTORAGE_PREFIX;
          APP_CONFIG.LOCALSTORAGE_ACTIVE = vars.REACT_APP_LOCALSTORAGE_ACTIVE;
          LOG.debug({APP_CONFIG});
        }

        // une fois les variables d'environnement ok, on lance l'init...

        // on met la tentative de connexion microsoft dans une fonction recursive
        // pour essayer 3 fois si echec
        const tryConnect = ({
          paramCode,
          paramSessionState,
          paramState,
          paramClienInfo,
          successCallback,
          errorCallback,
          tryNumber,
        }:{
          paramCode: string,
          paramSessionState: string,
          paramState: string,
          paramClienInfo: string,
          successCallback: ({accessToken,refreshToken}:{accessToken:string,refreshToken:string}) => any,
          errorCallback: ({error}:{error:any})=> any,
          tryNumber: number,
        }) => {
          connectMicrosoft({
            code: paramCode,
            session_state: paramSessionState,
            state: paramState,
            client_info: paramClienInfo,
            redirect_uri: window.location.pathname,
          })
          .then((loginDatas:I_LOGIN) => {
            accessToken = loginDatas?.access_token;
            refreshToken = loginDatas?.refresh_token;
            successCallback({accessToken,refreshToken});
          })
          .catch((error:any) => {
            console.error('Error:',error);
            // si 401, on essaye 3 fois
            if (error.errorCode === 401) {
              if (tryNumber <= 3) {
                tryConnect({
                  paramCode,
                  paramSessionState,
                  paramState,
                  paramClienInfo,
                  successCallback,
                  errorCallback,
                  tryNumber: tryNumber + 1,
                });
              }
              else {
                errorCallback({error});
              }
            }
            else {
              errorCallback({error});
            }
          });
        };

        // Connexion via SAML Microsoft // LocalStorage
        const envSAMLEnabled = APP_CONFIG.SAML_ENABLED === 'true';
        if (envSAMLEnabled) {
          // recuperation des paramètres de connexion renvoyés par saml
          const paramCode = readUrlParam('code');
          const paramSessionState = readUrlParam('session_state');
          const paramState = readUrlParam('state');
          const paramClienInfo = readUrlParam('client_info');
          if (!!paramCode && !!paramSessionState && !!paramState && !!paramClienInfo) {
            reducerDispatch({type:REDUCER_ACTIONS.SET_SAML_CONNECTING,payload:{samlConnecting: true}});
            setTimeout(() => {
              tryConnect({
                paramCode,
                paramSessionState,
                paramState,
                paramClienInfo,
                successCallback: ({accessToken,refreshToken}) => {
                  LOG.debug('SAML connexion success',{accessToken,refreshToken})
                  // stockage localstorage
                  setAccessTokenInLocalStorage(accessToken);
                  setRefreshTokenInLocalStorage(refreshToken);
                  // context
                  reducerDispatch({type:REDUCER_ACTIONS.SET_ACCESS_TOKEN,payload:{token:accessToken}});
                  reducerDispatch({type:REDUCER_ACTIONS.SET_REFRESH_TOKEN,payload:{token:refreshToken}});
                  // toast
                  toast(I18nService.getInstance().t('login.saml.success'),{type:'info'});
                  reducerDispatch({type:REDUCER_ACTIONS.SET_SAML_CONNECTING,payload:{samlConnecting: false}});
                  // on va charger les organisations en fonction de son role/function
                  getOrganisationsSets({
                      accessToken,
                      refreshToken,
                      dispatch:reducerDispatch,
                      state:reducerState,
                    })
                    .then((organisationsSets:I_ORGANISATIONS_SET[]) => {
                      applyUserOrganisations({
                        organisationsSets,
                        globalDispatch: reducerDispatch,
                        collaboratorsFiltersState: collaboratorsFiltersReducerState,
                        collaboratorsFiltersDispatch: collaboratorsFiltersReducerDispatch,
                        chargesFiltersState: chargesFiltersReducerState,
                        chargesFiltersDispatch: chargesFiltersReducerDispatch,
                        activitiesFiltersState: activitiesFiltersReducerState,
                        activitiesFiltersDispatch: activitiesFiltersReducerDispatch,
                        planificationFiltersState: planificationFiltersReducerState,
                        planificationFiltersDispatch: planificationFiltersReducerDispatch,
                      });
                    })
                    .catch((error:any) => {
                      //
                    });
                },
                errorCallback: ({error}) => {
                  LOG.error('SAML connexion error',{error})
                  toast(`${I18nService.getInstance().t('login.saml.success')} ${error.errorCode} - ${error.errorMessage.error_description}`,{type:'error'});
                  reducerDispatch({type:REDUCER_ACTIONS.SET_SAML_CONNECTING,payload:{samlConnecting: false}});
                },
                tryNumber: 1,
              });
            },300);
          }
          // pas de parametres de conexion
          else {
            // Récupération des données en local storage
            accessToken = getAccessTokenInLocalStorage();
            refreshToken = getRefreshTokenInLocalStorage();
            role = getRoleInLocalStorage();
            fonction = getFunctionInLocalStorage();
          }
        }
        // saml desactivé
        else {
          // Récupération des données en local storage
          LOG.debug('- RECUPERATION DES DONNEES EN LOCALSTORAGE (No SAML) --------');
          accessToken = getAccessTokenInLocalStorage();
          refreshToken = getRefreshTokenInLocalStorage();
          role = getRoleInLocalStorage();
          fonction = getFunctionInLocalStorage();
        }

        if (accessToken) {
          // on stocke les tokens dans le context
          reducerDispatch({type:REDUCER_ACTIONS.SET_ACCESS_TOKEN,payload:{token:accessToken}});
        }
        if (refreshToken) {
          reducerDispatch({type:REDUCER_ACTIONS.SET_REFRESH_TOKEN,payload:{token:refreshToken}});
        }
        if (role) {
          reducerDispatch({type:REDUCER_ACTIONS.SET_ROLE,payload:{role}});
        }
        if (fonction) {
          reducerDispatch({type:REDUCER_ACTIONS.SET_FUNCTION,payload:{function:fonction}});
        }

        // actions d'initialisation hello
        LOG.debug('- INITIALISATION DE L\'APP --------');
        getHello()
          .then((helloDatas:I_HELLO) => {
            reducerDispatch({type:REDUCER_ACTIONS.SET_ROLES,payload:{roles:helloDatas.roles}});
            reducerDispatch({type:REDUCER_ACTIONS.SET_ORGANISATIONS,payload:{organisations:helloDatas.organisations}});
            reducerDispatch({type:REDUCER_ACTIONS.SET_SITES,payload:{sites:helloDatas.sites}});
            reducerDispatch({type:REDUCER_ACTIONS.SET_FUNCTIONS,payload:{functions:helloDatas.functions}});
            reducerDispatch({type:REDUCER_ACTIONS.SET_REFERENTIALS,payload:{referentials:helloDatas.osac}});
            reducerDispatch({type:REDUCER_ACTIONS.SET_PAGINATION_LIMIT,payload:{pagination_limit:helloDatas.pagination_limit}});
            reducerDispatch({type:REDUCER_ACTIONS.SET_ROLES_PER_ENDPOINT,payload:{roles_per_endpoint:helloDatas.roles_per_endpoint}});
            reducerDispatch({type:REDUCER_ACTIONS.SET_INITIALIZATION_OK,payload:{initializationOK:true}});


            // on va charger les organisations en fonction de son role/function
            if (accessToken && refreshToken) {
              getOrganisationsSets({
                accessToken,
                refreshToken,
                dispatch:reducerDispatch,
                state:reducerState,
              })
              .then((organisationsSets:I_ORGANISATIONS_SET[]) => {
                applyUserOrganisations({
                  organisationsSets,
                  globalDispatch: reducerDispatch,
                  collaboratorsFiltersState: collaboratorsFiltersReducerState,
                  collaboratorsFiltersDispatch: collaboratorsFiltersReducerDispatch,
                  chargesFiltersState: chargesFiltersReducerState,
                  chargesFiltersDispatch: chargesFiltersReducerDispatch,
                  activitiesFiltersState: activitiesFiltersReducerState,
                  activitiesFiltersDispatch: activitiesFiltersReducerDispatch,
                  planificationFiltersState: planificationFiltersReducerState,
                  planificationFiltersDispatch: planificationFiltersReducerDispatch,
                });
              })
              .catch((error:any) => {
                LOG.error('Error getting user organisations:',error);
                reducerDispatch({type:REDUCER_ACTIONS.SET_ORGANISATIONS_SETS,payload:{organisationsSets:[]}});
                // si 401, tokens expirés
                if ('errorCode' in error && error.errorCode === 401/* && APP_CONFIG.AUTOREDIRECT_TO_LOGIN*/) {
                  // on peut pas navigate a ce niveau
                  // on reset les tokens
                  LOG.debug('- TOKENS EXPIRES, DECONNECTION --------');
                  reducerDispatch({type:REDUCER_ACTIONS.SET_ACCESS_TOKEN,payload:{token:undefined}});
                  reducerDispatch({type:REDUCER_ACTIONS.SET_REFRESH_TOKEN,payload:{token:undefined}});
                  reducerDispatch({type:REDUCER_ACTIONS.SET_ROLE,payload:{role:undefined}});
                  reducerDispatch({type:REDUCER_ACTIONS.SET_FUNCTION,payload:{function:undefined}});
                  setRefreshTokenInLocalStorage();
                  setAccessTokenInLocalStorage();
                  setRoleInLocalStorage();
                  setFunctionInLocalStorage();
                }
              })
              .finally(() => {
                // une fois que tout est chargé, on active l'app
                LOG.debug('- APP INITIALISEE --------');
                setAppLoaded(true);
              });
            }
            else {
              reducerDispatch({type:REDUCER_ACTIONS.SET_ORGANISATIONS_SETS,payload:{organisationsSets:[]}});
              // une fois que tout est chargé, on active l'app
              LOG.debug('- APP INITIALISEE --------');
              setAppLoaded(true);
            }
          })
          .catch((error:any) => {
            console.log('Mounting App, initialisation error:',error);
            reducerDispatch({type:REDUCER_ACTIONS.SET_INITIALIZATION_OK,payload:{initializationOK:false}});
          })
          .finally(() => {
            //
          });

      })
  },[]);

  if (!appLoaded) {
    return (
      <div className="min-h-screen w-full">
        <Centered>
          <ContentsLoading />
        </Centered>
      </div>
    );
  }

  if (!reducerState.initializationOK) {
    return (
      <div className="min-h-screen w-full">
        <Centered>
          <ContentsServerDown />
        </Centered>
      </div>
    );
  }

  // NB : il faut mettre le toastify en dehors du router, sinon il ne pop plus après un navigate
  return (
    <GlobalContext.Provider value={{state:reducerState,dispatch:reducerDispatch}}>
      <UsersFiltersContext.Provider value={{state:usersFiltersReducerState,dispatch:usersFiltersReducerDispatch}}>
      <CollaboratorsFiltersContext.Provider value={{state:collaboratorsFiltersReducerState,dispatch:collaboratorsFiltersReducerDispatch}}>
      <OOODaysFiltersContext.Provider value={{state:ooodaysFiltersReducerState,dispatch:ooodaysFiltersReducerDispatch}}>
      <BillingCustomersFiltersContext.Provider value={{state:billingCustomersFiltersReducerState,dispatch:billingCustomersFiltersReducerDispatch}}>
      <CustomersFiltersContext.Provider value={{state:customersFiltersReducerState,dispatch:customersFiltersReducerDispatch}}>
      <ChargesFiltersContext.Provider value={{state:chargesFiltersReducerState,dispatch:chargesFiltersReducerDispatch}}>
      <ActivitiesFiltersContext.Provider value={{state:activitiesFiltersReducerState,dispatch:activitiesFiltersReducerDispatch}}>
      <PlanificationFiltersContext.Provider value={{state:planificationFiltersReducerState,dispatch:planificationFiltersReducerDispatch}}>
      <ChargesReportContext.Provider value={{state:chargesReportReducerState,dispatch:chargesReportReducerDispatch}}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider value={customGlobalTheme}>
            <RouterProvider router={router} />
            <ToastContainer autoClose={4000} icon={false} style={{ zIndex: 10999 }} />
          </ThemeProvider>
        </QueryClientProvider>
      </ChargesReportContext.Provider>
      </PlanificationFiltersContext.Provider>
      </ActivitiesFiltersContext.Provider>
      </ChargesFiltersContext.Provider>
      </CustomersFiltersContext.Provider>
      </BillingCustomersFiltersContext.Provider>
      </OOODaysFiltersContext.Provider>
      </CollaboratorsFiltersContext.Provider>
      </UsersFiltersContext.Provider>
    </GlobalContext.Provider>
  );
}

export default App;
